import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useQuery, useMutation } from 'react-query';
import NavControls from '../components/NavControls';
import api from '../api';

const listInvites  = () => api.get('/users/invites/');
const deleteInvite = (code) => api.delete(`/users/invites/${code}/`);
const deleteUser   = (userId) => api.delete(`/users/${userId}/delete/`);

const generateInvite = ({ recipientName, phoneNumber }) =>
  api.post('/users/invites/generate/', {
    count: 1,
    recipient_name: recipientName,
    phone_number: phoneNumber,
  });

// Normalize to E.164 for the sms: URI (assumes US if 10 digits)
const toE164 = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  return `+${digits}`;
};

export default function InviteManager() {
  const [newCode, setNewCode]     = useState(null);
  const [recipientName, setName]  = useState('');
  const [phoneNumber, setPhone]   = useState('');

  const { data: inviteData, refetch } = useQuery(
    ['invites'],
    () => listInvites().then(r => r.data),
    { staleTime: 30000 }
  );
  const invites = inviteData || [];

  const generateMutation = useMutation(generateInvite, {
    onSuccess: (res) => {
      const code = res.data.codes[0];
      setNewCode(code);
      // If a phone number was entered, open native SMS app with invite pre-filled
      if (phoneNumber.replace(/\D/g, '').length >= 10) {
        const greeting = recipientName ? `${recipientName}! ` : '';
        const body = `${greeting}Do you like awesome music? What about instant playlists based on any combination of genre and year? Sign up below and enjoy!\n${registerUrl(code)}`;
        window.location.href = `sms:${toE164(phoneNumber)}?body=${encodeURIComponent(body)}`;
      }
      setName('');
      setPhone('');
      refetch();
    }
  });

  const deleteInviteMutation = useMutation(deleteInvite, { onSuccess: refetch });
  const deleteUserMutation   = useMutation(deleteUser,   { onSuccess: refetch });

  const handleGenerate = () => {
    generateMutation.mutate({ recipientName, phoneNumber });
  };

  const handleDeleteInvite = (code) => {
    if (!window.confirm(`Delete unused invite code "${code}"?`)) return;
    deleteInviteMutation.mutate(code);
  };

  const handleDeleteUser = (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This permanently removes their account.`)) return;
    deleteUserMutation.mutate(userId);
  };

  const registerUrl = (code) => `https://bbmusicmix.com/register?invite=${code}`;
  const hasSms = phoneNumber.replace(/\D/g, '').length >= 10;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1>Invite Manager</h1>
          <NavControls />
        </div>
        <p style={{ color: 'var(--text-muted)' }}>Generate invite codes and optionally send via text</p>
      </div>

      {/* ── Generate bar ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 32 }}>
        <input
          className="search-input"
          style={{ width: 160 }}
          placeholder="Name (optional)"
          value={recipientName}
          onChange={e => setName(e.target.value)}
        />
        <input
          className="search-input"
          style={{ width: 180 }}
          placeholder="Phone (optional)"
          value={phoneNumber}
          type="tel"
          onChange={e => setPhone(e.target.value)}
        />
        <button
          className="btn btn-primary"
          onClick={handleGenerate}
          disabled={generateMutation.isLoading}
        >
          {generateMutation.isLoading
            ? 'Generating…'
            : hasSms ? '📱 Generate & Text' : '+ Generate Invite'}
        </button>
      </div>


      {/* ── New invite QR card ────────────────────────────────────── */}
      {newCode && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--accent)',
          borderRadius: 12, padding: 24, marginBottom: 32,
          display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap',
        }}>
          <div style={{ background: 'white', padding: 16, borderRadius: 8 }}>
            <QRCodeSVG value={registerUrl(newCode)} size={180} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--accent)', marginBottom: 8 }}>
              New Invite Ready
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
              Share this QR code or link
            </div>
            <div style={{
              background: 'var(--bg3)', borderRadius: 6, padding: '10px 14px',
              fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)',
              wordBreak: 'break-all', marginBottom: 12,
            }}>
              {registerUrl(newCode)}
            </div>
            <button
              className="btn btn-secondary"
              style={{ fontSize: 12 }}
              onClick={() => navigator.clipboard.writeText(registerUrl(newCode))}
            >
              📋 Copy Link
            </button>
          </div>
        </div>
      )}

      {/* ── Invite table ─────────────────────────────────────────── */}
      <div style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
          All Invite Codes
        </h3>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="track-table" style={{ minWidth: 780 }}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Status</th>
                <th>Sent To</th>
                <th>Phone</th>
                <th>Used By</th>
                <th>Email</th>
                <th>Created</th>
                <th>QR</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {invites.map(invite => (
                <tr key={invite.code}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{invite.code}</td>
                  <td>
                    <span style={{ color: invite.used_by ? 'var(--text-muted)' : 'var(--accent)', fontSize: 12 }}>
                      {invite.used_by ? '✓ Used' : '● Available'}
                    </span>
                  </td>
                  <td style={{ fontSize: 13 }}>{invite.recipient_name || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{invite.phone_number || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{invite.used_by || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{invite.used_by_email || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {invite.created_at ? new Date(invite.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    {!invite.used_by && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 11, padding: '3px 8px' }}
                        onClick={() => setNewCode(invite.code)}
                      >
                        Show QR
                      </button>
                    )}
                  </td>
                  <td>
                    {invite.used_by ? (
                      <button
                        className="btn"
                        style={{
                          fontSize: 11, padding: '3px 8px',
                          background: 'rgba(220,50,50,0.15)', border: '1px solid rgba(220,50,50,0.4)',
                          color: '#ff6b6b',
                        }}
                        onClick={() => handleDeleteUser(invite.used_by_id, invite.used_by)}
                        disabled={deleteUserMutation.isLoading}
                      >
                        Delete User
                      </button>
                    ) : (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 11, padding: '3px 8px' }}
                        onClick={() => handleDeleteInvite(invite.code)}
                        disabled={deleteInviteMutation.isLoading}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {invites.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>
                    No invite codes yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
